/*
 * Lemonade Training Oy copy rights all right reserved.
 * Feature Flags Doc Type Definitions (F025 / ADR-0003, F023)
 *
 * Stored in Firestore: config/features — one pinned document (deliberately
 * NOT a match on config/{doc}; config/lemonade-config keeps its own story).
 * Owner-written only (console/script; rules: allow write: if false);
 * authenticated clients and the admin SDK read it.
 *
 * Every flag is optional, and absent or false means the flag is OFF, with one
 * exception: movementStateRead has no default and its reader throws when it
 * is absent (see its note).
 * What "off" costs differs per flag, so read each flag's own note before
 * assuming an empty document is safe: for classmateVisibility, off = the
 * feature is dark, which is the rollback state. For ptPrivacyEnforced, off =
 * the privacy doors are OPEN, which is the pre-flip state but NOT a safe
 * post-flip default. Flags with opposite fail directions share this document;
 * that is why the readers below differ in shape.
 */

import type { Timestamp } from "firebase/firestore";

export type FeatureFlagsDoc = {
  /**
   * F025 activation. Readers use the strict predicate `=== true` — absent,
   * false, or any non-boolean stays dark: getClassmates returns
   * { enabled: false } and the mobile announcement modal never shows.
   * Flipping to true is the owner-run activation; writing false is the
   * instant, free undo.
   */
  classmateVisibility?: boolean;

  /**
   * F023 M1. When true the callable doors (checkProgrammingAccess, the card
   * callables, assertCanManageProduct, the landing callables) deny
   * non-audience reads of isPT programmes. Firestore rules are flipped
   * separately, at the M1 flip. Absent or false = not enforcing, which is
   * both the pre-flip state and the rollback state.
   *
   * Cloud Functions read this through the admin SDK; no client reads it.
   */
  ptPrivacyEnforced?: boolean;

  /**
   * F026 M0, dashboard apply-template dialog. Absent or false = the dialog as it
   * was, which is both the pre-flip and the rollback state.
   */
  applyDialogAppliedBadge?: boolean;

  /**
   * F009. When true the mobile app may send diagnostics (EAS Observe
   * performance metrics and scrubbed errors), still subject to each device's
   * opt-out. Absent or false = nothing is sent, which is the rollback state.
   * Old builds without the diagnostics module ignore it.
   */
  mobileDiagnostics?: boolean;

  /** Mobile personal waiting-list state and leave controls. Missing or false stays dark. */
  mobileWaitlist?: boolean;

  /**
   * F023. When true the dashboard's gym-wide programme lists (membership link
   * pickers, a legacy box coach's copy-week gym list) query `isPT == false`
   * server-side; a caller's own programmes are never filtered. Absent or
   * false = the pre-flag query with PT filtered out on the client, which keeps legacy
   * programmes without an `isPT` value listed until the isPT backfill has
   * run. Turn on only after the backfill; must be on before the PT rules flip.
   */
  ptExcludedFromProgrammePickers?: boolean;

  /**
   * F023. The dashboard Workout Programming tab renders the lanes (Gym
   * programmes, My PT, PT from all coaches for admins, Archived), each lane
   * querying with an `isPT` pin. Absent or false = the flat list as it was,
   * which is the rollback state. Turn on only after the isPT backfill (the gym
   * lane drops programmes with no `isPT` value); must be on before the PT
   * rules flip, which denies the flat list to coaches.
   *
   * Also gates the mobile Coaching tab lanes and the Home staff lane (one key
   * for both surfaces by owner decision, so the undo is coupled). Mobile reads
   * flags once per session, so a flip reaches the app on its next sign-in or
   * cold start.
   */
  programmingLanes?: boolean;

  /**
   * F023. The programme page shows the PT trust note ("Only you, your coach
   * and the gym's admins can see this") on isPT programmes, on the dashboard
   * and in the mobile app (one key for both). Absent or false = no note,
   * which is the pre-flip and the rollback state. Turn on together with the
   * PT rules flip, never before: the sentence is only true once the rules
   * enforce it.
   */
  ptProgrammeTrustNote?: boolean;

  /** Dashboard staff week grid uses the lanes layout and one-line class cards. */
  scheduleBoardLanes?: boolean;

  /**
   * Dashboard staff schedule offers the Coach board view. Only reachable while
   * scheduleBoardLanes is also on, since the view toggle lives in that toolbar.
   */
  scheduleBoardCoach?: boolean;

  /**
   * Rooms, on BOTH surfaces: the box-admin Rooms manager and room columns on
   * the dashboard, and the room on mobile class cards and class details. One
   * key by owner decision (2026-09-21), so the undo is coupled — turning it on
   * so a gym can create rooms also shows those rooms to every athlete on a
   * build that carries them, and turning it off to hide them takes the
   * manager down too.
   */
  scheduleBoardRooms?: boolean;

  /**
   * Dashboard class panel shows the capacity stepper. Raising capacity there
   * moves people up from the waiting list. Absent or false hides the stepper;
   * nothing else sends a capacity to updateClass.
   */
  classPanelCapacity?: boolean;

  /**
   * When true, a membership that stops granting access (past due without
   * grace, dunning pause, unpaid, visit card expired or used up) loses its
   * linked programming's UserLibrary row, and recovery re-grants it. Absent
   * or false = lapses leave the row in place, which is the pre-flip and the
   * rollback state; cancellation and refund revoke as they always have.
   * Cloud Functions read this through the admin SDK; no client reads it.
   */
  linkedProgrammingLapseRevoke?: boolean;

  /**
   * When true, `updateClass` enforces a per-user call budget and answers
   * `resource-exhausted` beyond it. Absent or false = no budget, the rollback
   * state. Cloud Functions read this through the admin SDK; no client reads it.
   */
  rateLimitUpdateClass?: boolean;

  /**
   * When true, `workoutResultsLiked` enforces a per-sender call budget and
   * answers `resource-exhausted` beyond it. Absent or false = no budget, the
   * rollback state. Admin SDK only; no client reads it.
   */
  rateLimitWorkoutResultsLiked?: boolean;

  /**
   * F023 LD-1. When true a gym programme is managed (edited, archived, its
   * cards written) only by its creator and the gym's owner and admins; other
   * coaches keep read access. Read by the manage callables and the dashboard.
   * Absent or false = coaches with the programming grant manage colleagues'
   * programmes as before, which is the pre-flip and the rollback state. Turn
   * on together with the PT rules flip: this flag closes the callables only,
   * and those coaches' direct client writes (programme weeks, cards naming
   * themselves as coach) are refused only once the PT rules are live.
   */
  gymProgrammeEditRights?: boolean;

  /**
   * F028 MOVEMENT_STATE_READ: result reads come from the movementState
   * projection. No dark default: isMovementStateReadEnabled throws when the
   * key is absent or not a boolean, so production carries an explicit false.
   * Writing false is the undo; deleting the key while a reader is deployed is
   * an incident.
   */
  movementStateRead?: boolean;

  /**
   * F029, mobile only. Gates the "Add to calendar" button AND the automatic
   * calendar switch and its reconciler: one key by owner decision, so the only
   * undo for the automatic calendar also hides the button. Absent or false =
   * no calendar surface, the pre-flip and rollback state; events already in
   * members' calendars stay. Builds without the feature ignore it.
   */
  calendarSync?: boolean;

  /**
   * F029. When true, updateClass tells a moved class's members and coach
   * (push + email), resets the reminder dedupe fields and returns
   * rescheduleNotice. Absent or false = today's silent move, the pre-flip and
   * rollback state. Functions read it through readFeatureFlag; the dashboard
   * reads it only for the advisory pre-save line.
   */
  classRescheduleNotifications?: boolean;

  /**
   * F031. When true, a push token Expo reports as `DeviceNotRegistered` (on
   * the ticket or the receipt) is removed from that user's `expoPushTokens`.
   * Absent or false = shadow mode: the would-be removal is only logged, which
   * is the pre-flip and the rollback state. Cloud Functions read this through
   * the admin SDK; no client reads it.
   */
  pushTokenPruning?: boolean;

  /**
   * F031. When true, a token that Expo delivered to but that sits on more than
   * one user's `expoPushTokens` (a phone that changed hands without a clean
   * logout) is removed from every holder; the phone's current user re-adds it
   * on the next app start. Absent or false = shadow mode, only logged, which is
   * the pre-flip and the rollback state. Admin SDK only; no client reads it.
   * With pushRegistryOnly on, only the registry rule applies: the legacy and
   * mixed strip rules cannot occur.
   */
  pushSharedTokenCleanup?: boolean;

  /**
   * F031. When true, a push is sent with the Android channelId its type maps
   * to (PUSH_ANDROID_CHANNEL_BY_TYPE), so class reminders and waitlist pushes
   * use the app's high-importance channels. Absent or false = no channelId,
   * Expo's default channel, which is the pre-flip and the rollback state.
   * Cloud Functions read this through the admin SDK; no client reads it.
   */
  pushAndroidChannelId?: boolean;

  /**
   * F031, mobile only. Gates the in-app sheet that asks a member to turn on
   * notifications before the system prompt. Absent or false = no sheet, the
   * pre-flip and rollback state; the settings switch still asks the system.
   * Builds without the sheet ignore it.
   */
  pushConsentPrompt?: boolean;

  /**
   * F031. When true the app registers its push token through the
   * registerPushToken callable (PushTokens/{uid}, unreadable by gym staff)
   * instead of writing Users.expoPushTokens, and the callable accepts it.
   * Absent or false = the legacy write, the pre-flip and rollback state, and
   * registerPushToken refuses with failed-precondition; the server sends to
   * both places, so tokens already registered keep delivering after an undo.
   */
  pushTokenRegistry?: boolean;

  /**
   * F031. When true the daily push-token coverage counts a user as active by
   * Users.lastSeenAt; absent or false = by lastAppVersionAt, the pre-flip and
   * rollback state. Both are logged every run either way. Admin SDK only.
   */
  pushCoverageLastSeen?: boolean;

  /**
   * F031. When true, sends, pruning, shared-token cleanup and coverage read
   * only PushTokens/{uid} and ignore Users.expoPushTokens. Absent or false =
   * both places, the pre-flip and rollback state. Turn on only after
   * pushTokenRegistry is on and the legacy-token backfill has run in that
   * project. Admin SDK only.
   */
  pushRegistryOnly?: boolean;

  /**
   * When true, fist bumps honour the Social preference and mobile shows its switch.
   * Absent or false = the fist bump push as before, with no preference check or switch.
   */
  socialNotifications?: boolean;

  /** When the document was last hand-edited. Advisory only; nothing gates on it. */
  updatedAt?: Timestamp | Date;
};

/**
 * Whether the classmate list is live. Strict `=== true`: an absent document,
 * an absent key, `false`, or a hand-typed string "true" all stay dark. The
 * document is written by hand in the Firebase console, so a truthiness check
 * would let a typo'd string activate the feature for every member — the
 * opposite of the documented dark default and of the free undo.
 */
export function isClassmateVisibilityEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.classmateVisibility === true;
}

/** Whether the F026 apply-dialog slice is live. Strict `=== true`, as above. */
export function isApplyDialogAppliedBadgeEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.applyDialogAppliedBadge === true;
}

/** Whether mobile diagnostics may be sent. Strict `=== true`, like the classmate flag. */
export function isMobileDiagnosticsEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.mobileDiagnostics === true;
}

export function isMobileWaitlistEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.mobileWaitlist === true;
}

/** Whether the lanes week grid is on. Strict `=== true`, as above. */
export function isScheduleBoardLanesEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.scheduleBoardLanes === true;
}

/** Whether the Coach board view is on. Strict `=== true`, as above. */
export function isScheduleBoardCoachEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.scheduleBoardCoach === true;
}

/** Whether the Rooms manager is on. Strict `=== true`, as above. */
export function isScheduleBoardRoomsEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.scheduleBoardRooms === true;
}

/** Whether the class panel capacity stepper is on. Strict `=== true`, as above. */
export function isClassPanelCapacityEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.classPanelCapacity === true;
}

/** Whether a lapsed membership revokes its linked programming. Strict `=== true`, as above. */
export function isLinkedProgrammingLapseRevokeEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.linkedProgrammingLapseRevoke === true;
}

/** Whether the programme pickers exclude PT server-side. Strict `=== true`, as above. */
export function isPtExcludedFromProgrammePickersEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.ptExcludedFromProgrammePickers === true;
}

/** Whether the Workout Programming tab renders the lanes. Strict `=== true`, as above. */
export function isProgrammingLanesEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.programmingLanes === true;
}

/** Whether the PT trust note renders on the programme page. Strict `=== true`, as above. */
export function isPtProgrammeTrustNoteEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.ptProgrammeTrustNote === true;
}

/** Whether `updateClass` enforces its per-user call budget. Strict `=== true`, as above. */
export function isRateLimitUpdateClassEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.rateLimitUpdateClass === true;
}

/** Whether `workoutResultsLiked` enforces its per-sender call budget. Strict `=== true`, as above. */
export function isRateLimitWorkoutResultsLikedEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.rateLimitWorkoutResultsLiked === true;
}

/** Whether only creator, owner and admins manage a gym programme. Strict `=== true`, as above. */
export function isGymProgrammeEditRightsEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.gymProgrammeEditRights === true;
}

/** Whether the F029 calendar surfaces are on. Strict `=== true`, as above. */
export function isCalendarSyncEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.calendarSync === true;
}

/** Whether a moved class notifies its members. Strict `=== true`, as above. */
export function isClassRescheduleNotificationsEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.classRescheduleNotifications === true;
}

/** Whether dead push tokens are removed rather than only logged. Strict `=== true`, as above. */
export function isPushTokenPruningEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.pushTokenPruning === true;
}

/** Whether a token shared by several users is removed from all of them. Strict `=== true`, as above. */
export function isPushSharedTokenCleanupEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.pushSharedTokenCleanup === true;
}

/** Whether pushes carry their Android channelId. Strict `=== true`, as above. */
export function isPushAndroidChannelIdEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.pushAndroidChannelId === true;
}

/** Whether the mobile notification consent sheet may show. Strict `=== true`, as above. */
export function isPushConsentPromptEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.pushConsentPrompt === true;
}

/** Whether the app registers push tokens through the registry. Strict `=== true`, as above. */
export function isPushTokenRegistryEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.pushTokenRegistry === true;
}

/** Whether push-token coverage counts activity by lastSeenAt. Strict `=== true`, as above. */
export function isPushCoverageLastSeenEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.pushCoverageLastSeen === true;
}

/** Whether push code reads only the token registry. Strict `=== true`, as above. */
export function isPushRegistryOnlyEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.pushRegistryOnly === true;
}

/** Whether fist bumps go through the notification service. Strict `=== true`, as above. */
export function isSocialNotificationsEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.socialNotifications === true;
}

/**
 * Whether result reads use the movementState projection. Fail-loud, unlike its
 * siblings: a cutover that silently picks a side is the failure to avoid.
 */
export function isMovementStateReadEnabled(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  if (flags == null) {
    throw new Error(
      "config/features is unavailable, so movementStateRead cannot be read",
    );
  }
  const value = flags.movementStateRead;
  if (typeof value !== "boolean") {
    throw new Error(
      `config/features.movementStateRead must be a boolean (got ${value === undefined ? "absent" : typeof value})`,
    );
  }
  return value;
}

/**
 * Client-side read of movementStateRead for UI that only explains the new
 * scoring. A client cannot fail loud, so anything but `true` reads as off;
 * the server keeps using isMovementStateReadEnabled.
 */
export function isMovementStateReadLive(
  flags: FeatureFlagsDoc | null | undefined,
): boolean {
  return flags?.movementStateRead === true;
}

/**
 * Whether the PT-privacy callable doors are enforcing. Strict `=== true` on a
 * present document, for the same reason as above: the document is
 * hand-written, and a typo'd string must not decide enforcement for every gym.
 *
 * `whenUnavailable` is required, and named at the call site, deliberately. An
 * unreadable document is not the same event as a flag that is off, but the
 * obvious reader shape (`snap.exists ? snap.data() : null`) collapses them
 * into one null. Making the caller spell out its fail direction means a
 * Firestore blip cannot silently un-enforce PT privacy just because nobody
 * thought about it: pass false before the flip, true after, so the doors fail
 * closed once enforcement is the expected state. It reads as
 * `{ whenUnavailable: true }` rather than a bare `true` so a reviewer can see
 * which way a call fails without opening this file.
 *
 * Only a missing document takes that value. A document that exists with the
 * flag absent is off, always.
 */
export function isPtPrivacyEnforced(
  flags: FeatureFlagsDoc | null | undefined,
  opts: { whenUnavailable: boolean },
): boolean {
  return flags == null
    ? opts.whenUnavailable
    : flags.ptPrivacyEnforced === true;
}
